/**
 * The game database: typed views over the excel tables read from the player's own discs.
 * Shared by the realm server (authoritative rules) and the browser (rendering, offline play).
 * Nothing here contains game data — only the shape of the tables.
 */
import { type ArchiveSet, type ExcelRow, ExcelTable } from '@d2/formats';
import { buildOverlays, buildStates, type OverlayDef, type StateDef } from './effects';

export * from './effects';
export * from './fingerprint';

export const TABLES = [
  'Levels', 'LvlPrest', 'LvlTypes', 'LvlMaze', 'LvlWarp', 'LvlSub',
  'monstats', 'monstats2', 'MonLvl', 'MonType', 'MonMode', 'SuperUniques', 'MonItemPercent',
  'charstats', 'experience', 'PlrMode', 'PlayerClass',
  'skills', 'skilldesc', 'Missiles', 'states', 'Overlay', 'ElemTypes',
  'weapons', 'armor', 'misc', 'ItemTypes', 'ItemStatCost', 'Properties',
  'MagicPrefix', 'MagicSuffix', 'RarePrefix', 'RareSuffix', 'UniqueItems', 'SetItems', 'Sets',
  'TreasureClassEx', 'itemratio', 'qualityitems', 'lowqualityitems', 'gems', 'Runes', 'automagic',
  'objects', 'ObjMode', 'objgroup', 'shrines', 'npc', 'inventory', 'belts', 'difficultylevels', 'hireling',
  'UniquePrefix', 'UniqueSuffix', 'UniqueAppellation',
  'Sounds', 'SoundEnviron', 'bodylocs', 'WeaponClass', 'Composit', 'ArmType', 'HitClass', 'StorePage', 'books',
  'cubemain', 'cubemod', 'cubetype', 'gamble',
  'MonUMod', 'MonProp', 'MonSeq', 'MonEquip',
] as const;
export type TableName = (typeof TABLES)[number];

export type Difficulty = 0 | 1 | 2;
const DIFF_SUFFIX = ['', '(N)', '(H)'] as const;

/**
 * 1.10 renamed every monstats class ("Zombie" → "zombie1", "Kaelan" → "act2guard2"). The engine
 * code this project recreates names a few classes (town NPCs, quest units, summons, sound
 * families) by their 1.07–1.09 names; later data's rows at these engine indices keep those names
 * as their `id`, so the rest of the code reads either version alike. The table's own id stays in
 * `tableId` and both find the row in `monById`.
 */
const LEGACY_CLASS: Readonly<Record<number, string>> = {
  0: 'Skeleton', 5: 'Zombie', 19: 'Fallen', 25: 'Yeti', 29: 'SandRaider', 39: 'Wraith', 58: 'FallenShaman', 64: 'SpikeFiend',
  68: 'SandMaggot', 102: 'Guardian', 134: 'BloodLord', 142: 'Fetish', 146: 'DeckardCain', 155: 'Warriv', 175: 'Warriv',
  190: 'SandMaggotEgg', 210: 'Meshif', 244: 'DeckardCain', 245: 'DeckardCain', 246: 'DeckardCain', 251: 'tyrael', 264: 'meshif',
  265: 'DeckardCain', 279: 'FetishShaman', 331: 'Kaelan', 338: 'Guard', 345: 'Council Member', 346: 'Council Member',
  347: 'Council Member', 351: 'Hydra', 352: 'Hydra', 353: 'Hydra', 367: 'tyrael', 397: 'Fetish', 402: 'The Smith',
  409: 'The Feature Creep', 410: 'Wake of Destruction', 411: 'Charged Bolt Sentry', 412: 'Lightning Sentry', 413: 'Blade Creeper',
  415: 'Inferno Sentry', 416: 'Death Sentry', 417: 'Shadow Warrior', 418: 'Shadow Master', 419: 'Druid Hawk',
  420: 'Druid Spirit Wolf', 421: 'Druid Fenris', 422: 'Spirit of Barbs', 423: 'Heart of Wolverine', 424: 'Oak Sage',
  425: 'Druid Plague Poppy', 426: 'Druid Cycle of Life', 427: 'Vine Creature', 428: 'Druid Bear', 436: 'RotWalker',
  514: 'Nihlathak Town', 520: 'DeckardCain', 521: 'tyrael', 540: 'Ancient Barbarian 1', 541: 'Ancient Barbarian 2',
  542: 'Ancient Barbarian 3', 543: 'Baal Throne', 544: 'Baal Crab', 557: 'Council Member', 562: 'Baal Tentacle',
  563: 'Baal Tentacle', 564: 'Baal Tentacle', 565: 'Baal Tentacle', 566: 'Baal Tentacle', 570: 'Baal Crab Clone',
};

/** 1.07–1.09 monstats "Mode<x>" flags → the mode tokens of later monstats2 "m<token>" columns. */
const MODE_TOKEN: Record<string, string> = { DH: 'DT', N: 'NU', W: 'WL', GH: 'GH', A1: 'A1', A2: 'A2', B: 'BL', C: 'SC', S1: 'S1', S2: 'S2', S3: 'S3', S4: 'S4', DD: 'DD', KB: 'KB', SQ: 'SQ', RN: 'RN' };

// ---------------------------------------------------------------------------------------------
// Typed record shapes (only fields the engine reads directly; `row` keeps the rest reachable).

export interface LevelDef {
  id: number;
  name: string; // internal name, e.g. "Act 1 - Town"
  levelName: string; // display name
  warpName: string; // "To The Blood Moor"
  entryFile: string; // UI sound/picture on entry
  act: number; // 0-based
  drlg: 1 | 2 | 3; // maze, preset, outdoor
  levelType: number;
  sizeX: [number, number, number];
  sizeY: [number, number, number];
  offsetX: number;
  offsetY: number;
  depend: number;
  isInside: boolean;
  vis: number[]; // 8 neighbour level ids (0 = none)
  warp: number[]; // LvlWarp ids, paired with vis
  waypoint: number; // 255 = none
  monLvl: [number, number, number];
  /** Area level in expansion games (Levels MonLvl1Ex.., 1.09+; 0 where the data has none). */
  monLvlEx: [number, number, number];
  monDen: number;
  uniqueMin: number;
  uniqueMax: number;
  monsters: string[]; // M1..M25 monstats ids
  specials: string[]; // S1..
  uniques: string[]; // U1..
  /** The level's spawn lists and densities per difficulty, as monstats rows (see {@link LevelSpawn}). */
  spawn: LevelSpawn;
  critters: { id: string; chance: number }[];
  light: { intensity: number; r: number; g: number; b: number };
  isTown: boolean;
  soundEnv: number;
  objGroups: { group: number; prob: number }[];
  quest: number;
  rain: boolean;
  row: ExcelRow;
}

/**
 * What a level spawns, per difficulty (normal, nightmare, hell), as monstats row indices. 1.07–1.09
 * have one density and one list for every difficulty (M1..Mtot, monstats PopulateIds, "0" being
 * PopulateId 0 — see `resolveLevelMonsters`); later data has
 * per-difficulty densities and a separate nightmare/hell list. Both are read into this shape.
 */
export interface LevelSpawn {
  /** Levels.txt MonDen: chance out of 100000 per 3×3-subtile cell of a room to start a group. */
  density: [number, number, number];
  uniqueMin: [number, number, number];
  uniqueMax: [number, number, number];
  /** Monster list for normal games and for nightmare/hell (rows, in table order). */
  normal: number[];
  nightmare: number[];
  /** The nest that may stand in for each `normal` slot (1.07 S1..; -1 = none), e.g. crow nests. */
  nests: number[];
  /** Champion/unique pool in normal games (U1.. / umon). */
  uniques: number[];
  /**
   * How many of the list's monsters a game draws for the level (later data's NumMon); null when
   * the table has no such column (1.07/1.09 data) and the engine's own count applies.
   */
  numMon: number | null;
  /**
   * 1.07-format tables (Mtot lists) store monstats Rarity one below the weight the spawner draws
   * with (every spawnable row: later data's Rarity = 1.07 Rarity + 1).
   */
  legacyRarity: boolean;
}

export interface PresetDef {
  def: number;
  name: string;
  levelId: number;
  populate: boolean;
  outdoors: boolean;
  killEdge: boolean;
  fillBlanks: boolean;
  sizeX: number;
  sizeY: number;
  scan: boolean;
  files: string[]; // archive paths of the .ds1 variants
  dt1Mask: number;
  row: ExcelRow;
}

export interface LvlTypeDef {
  id: number;
  name: string;
  act: number;
  files: string[]; // archive paths of .dt1 files, indexed by File N (1-based in the table)
}

export interface MazeDef {
  level: number;
  rooms: [number, number, number];
  sizeX: number;
  sizeY: number;
  merge: number;
}

export interface WarpDef {
  id: number;
  name: string;
  selectX: number;
  selectY: number;
  selectDX: number;
  selectDY: number;
  exitWalkX: number;
  exitWalkY: number;
  offsetX: number;
  offsetY: number;
  litVersion: boolean;
  tiles: number;
  direction: string;
}

export interface MonStat {
  hcIdx: number;
  /** Class id ("Zombie"; later data: "zombie1", or the 1.09 name for the rows in `LEGACY_CLASS`). */
  id: string;
  /** The class id as the table writes it (equals `id` except for `LEGACY_CLASS` rows). */
  tableId: string;
  baseId: string;
  nextInClass: string;
  nameStr: string;
  code: string; // animation token, e.g. "ZM"
  monType: string;
  ai: string;
  enabled: boolean;
  velocity: number;
  run: number;
  level: [number, number, number];
  minGrp: number;
  maxGrp: number;
  sizeX: number;
  sizeY: number;
  meleeRange: number;
  rarity: number;
  components: Record<string, string[]>; // HD → ["lit","med"] options
  baseW: string;
  modes: Record<string, boolean>; // which modes the monster has (DH,N,W,GH,A1,A2,B,C,S1-4,DD,KB,SQ,RN)
  isMelee: boolean;
  isNpc: boolean;
  inTown: boolean;
  isCritter: boolean;
  isAttackable: boolean;
  isSelectable: boolean;
  killable: boolean;
  noMap: boolean;
  shadow: boolean;
  light: [number, number, number, number];
  boss: boolean;
  undead: 0 | 1 | 2; // none, low, high
  demon: boolean;
  missA1: string;
  missA2: string;
  missS1: string;
  skills: { skill: string; level: number; mode: string }[];
  hp: [[number, number], [number, number], [number, number]];
  ac: [number, number, number];
  exp: [number, number, number];
  toBlock: number;
  a1: [Dmg, Dmg, Dmg];
  a2: [Dmg, Dmg, Dmg];
  s1: [Dmg, Dmg, Dmg];
  resist: [Resists, Resists, Resists];
  treasure: [string[], string[], string[]];
  aiParams: number[];
  drain: number;
  coldEffect: number;
  pixHeight: number;
  interact: boolean;
  heightOverlay: number;
  deathDamage: number;
  hitClass: number;
  /** Skills by skills.txt Id (1.07 numbers them), with level and animation sequence. */
  skillIds: { id: number; level: number; seq: number }[];
  /** Missiles.txt rows fired by each attack mode (-1 = none). */
  missiles: { A1: number; A2: number; S1: number; S2: number; S3: number; S4: number; C: number; SQ: number };
  /** Elemental damage added to an attack mode (ElMode/ElType/ElOver/ElPct/ElMinD/ElMaxD/ElDur). */
  elem: { mode: number; type: number; overlay: number; pct: number; min: number; max: number; dur: number };
  /** 0 = enemy, 1 = player side (summons, hirelings), 2 = neutral (bone walls). */
  align: number;
  petIgnore: boolean;
  noUniqueShift: boolean;
  /**
   * Life, defense, experience and attack values are percentages of MonLvl.txt's for the
   * monster's level (1.10 and later; see `GameData.monsterValues`), not the values themselves.
   */
  ratios: boolean;
  row: ExcelRow;
  /** The row holding the graphics columns: monstats2's in later data, else `row`. */
  gfx: ExcelRow;
}
/** A monster's values at its level and difficulty (`GameData.monsterValues`). */
export interface MonValues {
  hp: [number, number];
  ac: number;
  exp: number;
  a1: Dmg;
  a2: Dmg;
  s1: Dmg;
  /** Elemental attack damage, min and max. */
  elem: [number, number];
}
/** The values as monstats holds them (1.07–1.09: the values themselves; later data: percentages). */
export function tableMonsterValues(stat: MonStat, difficulty: number): MonValues {
  const d = Math.max(0, Math.min(2, difficulty));
  return { hp: stat.hp[d], ac: stat.ac[d], exp: stat.exp[d], a1: stat.a1[d], a2: stat.a2[d], s1: stat.s1[d], elem: [stat.elem.min, stat.elem.max] };
}

export interface Dmg {
  min: number;
  max: number;
  toHit: number;
}
export interface Resists {
  dmg: number;
  magic: number;
  fire: number;
  light: number;
  cold: number;
  poison: number;
}

export interface ClassStats {
  index: number; // charstats / experience column order
  name: string;
  str: number;
  dex: number;
  int: number;
  vit: number;
  stamina: number;
  hpAdd: number;
  manaRegen: number;
  toHitFactor: number;
  walkVelocity: number;
  runVelocity: number;
  runDrain: number;
  lifePerLevel: number; // quarter points
  staminaPerLevel: number;
  manaPerLevel: number;
  lifePerVitality: number;
  staminaPerVitality: number;
  manaPerMagic: number;
  blockFactor: number;
  startSkill: number;
  baseWClass: string;
  startItems: { code: string; loc: string; count: number }[];
}

export interface SkillDef {
  id: number;
  name: string;
  charClass: string; // 'ama', ... or ''
  anim: string;
  monAnim: string;
  seqNum: number;
  isAura: boolean;
  passive: boolean;
  range: string; // 'none' | 'h2h' | 'rng' | 'both' | 'loc'
  reqLevel: number;
  reqSkills: string[];
  page: number;
  /** Position in the skill tree panel. */
  treeRow: number;
  treeColumn: number;
  iconCel: number;
  leftSkill: boolean;
  mana: number;
  lvlMana: number;
  manaShift: number;
  minMana: number;
  inTown: boolean;
  delay: number;
  params: number[];
  toHit: number;
  levToHit: number;
  hitShift: number;
  srcDam: number;
  minDam: number;
  maxDam: number;
  /** Damage added per level, per bracket (see `levelBrackets`): to the minimum, and to the maximum. */
  levDam: readonly number[];
  levDamMax: readonly number[];
  eType: string;
  eMin: number;
  eMax: number;
  /** Elemental damage added per level, per bracket: to the minimum, and to the maximum. */
  eLev: readonly number[];
  eLevMax: readonly number[];
  eLen: number;
  eLevLen: [number, number, number];
  srvMissile: string;
  state: string;
  attackRank: number;
  itemClasses: string[];
  /** Row index: the number in the skill's string keys (skillname<n>, skillsd<n>, skillld<n>) in 1.07–1.09. */
  strIndex: number;
  /** String keys of the skill's name and long description (later data names them in skilldesc.txt). */
  nameKey: string;
  descKey: string;
  /** caster state column (e.g. STATE_FROZENARMOR) and State1-3. */
  casterState: string;
  states: string[];
  selectDead: boolean;
  searchMonsterNear: boolean;
  targetableOnly: boolean;
  targetPet: boolean;
  targetAlly: boolean;
  interrupt: boolean;
  periodic: boolean;
  /** Finishing move: releases the assassin's charge-ups. */
  finishing: boolean;
  /** Usable by monsters (skills.txt "Monster" flag). */
  monster: boolean;
  row: ExcelRow;
  /** The skill's skilldesc.txt row (later data): tooltip lines and their calc columns. */
  desc?: ExcelRow;
}

export interface MissileDef {
  id: number;
  name: string;
  vel: number;
  maxVel: number;
  accel: number;
  range: number;
  levRange: number;
  light: number;
  rgb: [number, number, number];
  celFile: string;
  animLen: number;
  animSpeed: number;
  loopAnim: boolean;
  collideType: number;
  collideKill: boolean;
  size: number;
  explosion: boolean;
  pierce: boolean;
  trans: number;
  numDirections: number;
  subMissile: string;
  explosionMissile: string;
  hitClass: number;
  minDamage: number;
  maxDamage: number;
  /** Per-level damage brackets, as on `SkillDef`. */
  levDam: readonly number[];
  levDamMax: readonly number[];
  eType: string;
  eMin: number;
  eMax: number;
  eLev: readonly number[];
  eLevMax: readonly number[];
  eLen: number;
  srcDamage: number;
  /** Half2HSrc: the skill's weapon share is halved with a two-handed weapon (Blade Fury, Blade Sentinel). */
  half2HSrc: boolean;
  skill: string;
  params: [number, number];
  /** Can hit the same unit again after `nextDelay` ticks (novas, auras of fire, clouds). */
  nextHit: boolean;
  nextDelay: number;
  /**
   * Missiles.txt ToHit: the missile rolls to hit when it reaches a unit (arrows, spikes, javelins);
   * without it it always connects (spits, bolts, novas). Tables without the column: true.
   */
  toHit: boolean;
  collideFriend: boolean;
  qty: number;
  hitShift: number;
  eLen2: [number, number, number];
  row: ExcelRow;
}

export type ItemCategory = 'weapon' | 'armor' | 'misc';
export interface ItemBase {
  code: string;
  name: string; // string-table key
  category: ItemCategory;
  type: string; // ItemTypes code
  type2: string;
  level: number;
  levelReq: number;
  rarity: number;
  spawnable: boolean;
  cost: number;
  /** Price at the gambling vendor (armor/weapons/misc "gamble cost"); 0 when the table has none. */
  gambleCost: number;
  invWidth: number;
  invHeight: number;
  invFile: string;
  uniqueInvFile: string;
  setInvFile: string;
  flippyFile: string;
  alternateGfx: string;
  component: number;
  wclass: string;
  wclass2h: string;
  minDam: number;
  maxDam: number;
  twoHandMinDam: number;
  twoHandMaxDam: number;
  minMisDam: number;
  maxMisDam: number;
  oneOrTwoHanded: boolean;
  twoHanded: boolean;
  rangeAdder: number;
  speed: number;
  strBonus: number;
  dexBonus: number;
  reqStr: number;
  reqDex: number;
  durability: number;
  noDurability: boolean;
  minAc: number;
  maxAc: number;
  block: number;
  gemSockets: number;
  stackable: boolean;
  minStack: number;
  maxStack: number;
  spawnStack: number;
  useable: boolean;
  belt: number;
  autoBelt: boolean;
  quest: number;
  normCode: string;
  uberCode: string;
  ultraCode: string;
  magicLvl: number;
  hitClass: string;
  missileType: number;
  /** Armor component tokens for composite rendering: rArm, lArm, Torso, Legs, rSPad, lSPad. */
  armorTokens: number[];
  vendors: Record<string, { min: number; max: number; magicMin: number; magicMax: number; magicLvl: number }>;
  row: ExcelRow;
}

export interface ItemTypeDef {
  code: string;
  name: string;
  equiv: string[];
  bodyLocs: string[];
  beltable: boolean;
  throwable: boolean;
  quiver: string;
  shoots: string;
  normal: boolean;
  magic: boolean;
  rare: boolean;
  maxSockets: [number, number, number];
  staffMods: string;
  cls: string;
  varInvGfx: number;
  invGfx: string[];
  storePage: string;
  autoStack: boolean;
  repair: boolean;
  identify: boolean;
  charm: boolean;
  gem: boolean;
  treasureClass: boolean;
  rarity: number;
}

export interface StatDef {
  id: number;
  name: string;
  add: number;
  multiply: number;
  divide: number;
  valShift: number;
  saveBits: number;
  saveAdd: number;
  percent: boolean;
  perLevelShift: number;
  descPriority: number;
  descFunc: number;
  descVal: number;
  descStrPos: string;
  descStrNeg: string;
  descStr2: string;
  dgrp: number;
  dgrpFunc: number;
  dgrpVal: number;
  dgrpStrPos: string;
  dgrpStrNeg: string;
  dgrpStr2: string;
  row: ExcelRow;
}

export interface PropertyDef {
  code: string;
  funcs: { set: number; val: number; func: number; stat: string }[];
  row: ExcelRow;
}

export interface Mod {
  code: string;
  param: string;
  min: number;
  max: number;
}

export interface AffixDef {
  index: number;
  name: string; // string key
  spawnable: boolean;
  rare: boolean;
  level: number;
  maxLevel: number;
  levelReq: number;
  classSpecific: string;
  frequency: number;
  group: number;
  mods: Mod[];
  itypes: string[];
  etypes: string[];
  transformColor: string;
}

export interface RareAffixDef {
  index: number;
  name: string;
  itypes: string[];
  etypes: string[];
}

export interface UniqueDef {
  index: number;
  name: string;
  enabled: boolean;
  rarity: number;
  level: number;
  levelReq: number;
  code: string;
  invTransform: string;
  invFile: string;
  mods: Mod[];
  /** Later data: a ladder-only unique (UniqueItems `ladder`, see the sim's content gates). */
  ladder: boolean;
  /** Later data: only one may be carried (UniqueItems `carry1`: the unique charms). */
  carry1: boolean;
}

export interface SetItemDef {
  index: number;
  name: string;
  set: string;
  code: string;
  rarity: number;
  level: number;
  levelReq: number;
  invFile: string;
  mods: Mod[];
  partialMods: Mod[]; // aprop1a..
  /**
   * Later data: the partial bonuses grouped as the engine applies them (aprop1a/b, aprop2a/b…),
   * and SetItems `add func`: 0 = always on, 1 = group k with the set's k-th other item worn,
   * 2 = group k with k + 1 items of the set worn.
   */
  partialGroups?: Mod[][];
  addFunc?: number;
  /** Source row (for 1.07 data: the whole set's row) and the item's slot in it (1-6), if any. */
  row?: ExcelRow;
  slot?: number;
  /** 1.07 layout: bonuses for wearing n items (PCode2..5, keyed by n) and the full set (FCode*). */
  setMods?: { count: number; mods: Mod[] }[];
}

/** A socket filler's bonuses (gems.txt): one mod list per kind of parent item. */
export interface GemDef {
  code: string;
  /** gems.txt row. */
  index: number;
  name: string;
  /** Rune letter ("El"), shown in runeword tooltips; '' for gems. */
  letter: string;
  mods: { weapon: Mod[]; helm: Mod[]; shield: Mod[] };
}

/** A Runes.txt row. */
export interface RunewordDef {
  /** Runes.txt row. */
  index: number;
  /** String-table key ("Runeword1"). */
  key: string;
  /** Display name from the table, used when the string table lacks the key. */
  name: string;
  /** "complete" column: only finished runewords can form. */
  complete: boolean;
  /** Later data: `server` — a ladder-only runeword (Battle.net ladder games only). */
  server: boolean;
  itypes: string[];
  etypes: string[];
  /** Rune item codes, in socket order. */
  runes: string[];
  mods: Mod[];
}

export interface TreasureClass {
  name: string;
  picks: number;
  unique: number;
  set: number;
  rare: number;
  magic: number;
  noDrop: number;
  items: { item: string; prob: number }[];
  group: number;
  level: number;
}

export interface ObjectDef {
  id: number;
  name: string;
  token: string;
  sizeX: number;
  sizeY: number;
  frameCnt: number[];
  frameDelta: number[];
  cycleAnim: boolean[];
  hasCollision: boolean[];
  lit: number[];
  start: number[];
  selectable: boolean[];
  isDoor: boolean;
  blocksVis: boolean;
  orientation: number;
  trans: number;
  operateRange: number;
  operateFn: number;
  populateFn: number;
  initFn: number;
  clientFn: number;
  shrineFunction: number;
  draw: number;
  rgb: [number, number, number];
  components: Record<string, boolean>;
  xOffset: number;
  yOffset: number;
  subClass: number;
  autoMap: number;
  drawUnder: boolean;
  blockMissile: boolean;
  openWarp: boolean;
  modeNames: string[];
  row: ExcelRow;
}

// ---------------------------------------------------------------------------------------------

const ITEM_VENDORS = ['Charsi', 'Gheed', 'Akara', 'Fara', 'Lysander', 'Drognan', 'Hralti', 'Alkor', 'Ormus', 'Elzix', 'Asheara', 'Cain', 'Halbu', 'Jamella', 'Larzuk', 'Drehya', 'Malah'];

/**
 * Names 1.10 respelled beyond case and separators (1.09 loose name → later loose name), for the
 * ones the engine code refers to: Andariel's spray missile and a few overlays.
 */
const RESPELLED: Readonly<Record<string, string>> = {
  andrial: 'andarielspray',
  cursedecrpify: 'cursedecrepify',
  curselowerresistance: 'curselowerresist',
  oaksageoverlay: 'oaksage',
};

/** A name with the parts the versions spell differently removed (case, prefixes, separators). */
export function looseName(name: string): string {
  const l = name.toLowerCase().replace(/^(state|stats)_/, '').replace(/[^a-z0-9]/g, '').replace(/^shrineshimmer(?=[a-z])/, 'shrine');
  return RESPELLED[l] ?? l;
}

/**
 * Name → row lookup (keys lower case) that also answers the other version's spelling. 1.10
 * renamed many internal names: states lost their "STATE_" prefix ("STATE_FREEZE" → "freeze"),
 * stats their "STATS_", overlays and many missiles went lower case with underscores
 * ("AuraMightFront" → "aura_might_front", "CRArrow1" → "cr_arrow1"). A key that is not found as
 * given is looked up by its `looseName`. Names that changed beyond that (e.g. the missile
 * "Andrial" → "andarielspray") are not matched.
 */
export class NameIndex<V> extends Map<string, V> {
  private readonly loose = new Map<string, V>();
  override set(key: string, v: V): this {
    const l = looseName(key);
    if (!this.loose.has(l)) this.loose.set(l, v);
    return super.set(key, v);
  }
  override get(key: string): V | undefined {
    return super.get(key) ?? super.get(key.toLowerCase()) ?? this.loose.get(looseName(key));
  }
  override has(key: string): boolean {
    return this.get(key) !== undefined;
  }
}

/**
 * Per-level increments of a skill or missile column, one per level bracket: 2–8, 9–16, 17–22,
 * 23–28 and 29+. 1.07–1.09 have three brackets (2–8, 9–16, 17+; `<old>1..3`, shared by the
 * minimum and maximum), later data five per bound (`<later>1..5`). Three-bracket data repeats
 * its last bracket, so both read the same way.
 */
function levelBrackets(r: ExcelRow, old: string, later: string): number[] {
  if (r.has(`${later}1`)) return [1, 2, 3, 4, 5].map((k) => r.int(`${later}${k}`));
  const v = [1, 2, 3].map((k) => r.int(`${old}${k}`));
  return [...v, v[2], v[2]];
}

const dt1Path = (f: string) => `data\\global\\tiles\\${f.replace(/\//g, '\\')}`;

/**
 * A Levels.txt row's spawn lists and densities. 1.07: MonDen/MonUMin/MonUMax for every difficulty,
 * M1..M<Mtot> (PopulateIds, "0" being id 0), S1.. (nest per M slot), U1..U<Utot>. Later data:
 * per-difficulty columns "(N)"/"(H)" and mon1../nmon1../umon1.. class ids. The lists are kept raw
 * here and turned into monstats rows once monstats is loaded.
 */
function levelSpawn(r: ExcelRow): LevelSpawn {
  const perDiff = (col: string): [number, number, number] => {
    const n = r.int(col);
    return [n, r.has(`${col}(N)`) ? r.int(`${col}(N)`) : n, r.has(`${col}(H)`) ? r.int(`${col}(H)`) : n];
  };
  const take = (prefix: string, count: number) => Array.from({ length: count }, (_, i) => r.str(`${prefix}${i + 1}`));
  const nonEmpty = (prefix: string, max: number) => take(prefix, max).filter((v) => v && v !== '0');
  let raw: { normal: string[]; nightmare: string[]; nests: string[]; uniques: string[] };
  if (r.has('Mtot')) {
    const m = r.int('Mtot'), u = r.has('Utot') ? r.int('Utot') : 0;
    raw = { normal: take('M', m).map((v) => v || '0'), nightmare: [], nests: take('S', m), uniques: take('U', u).map((v) => v || '0') };
  } else {
    raw = { normal: nonEmpty('mon', 25), nightmare: nonEmpty('nmon', 25), nests: [], uniques: nonEmpty('umon', 25) };
    if (!raw.normal.length) raw = { normal: nonEmpty('M', 25), nightmare: [], nests: take('S', 25), uniques: nonEmpty('U', 25) };
  }
  const spawn: LevelSpawn & { raw?: typeof raw } = {
    numMon: r.has('NumMon') ? r.int('NumMon') : null,
    legacyRarity: r.has('Mtot'),
    density: perDiff('MonDen'),
    uniqueMin: perDiff('MonUMin'),
    uniqueMax: perDiff('MonUMax'),
    normal: [],
    nightmare: [],
    nests: [],
    uniques: [],
    raw,
  };
  return spawn;
}

export class GameData {
  readonly t = {} as Record<TableName, ExcelTable>;
  readonly levels: LevelDef[] = [];
  readonly presets = new Map<number, PresetDef>();
  readonly lvlTypes: LvlTypeDef[] = [];
  readonly mazes = new Map<number, MazeDef>();
  readonly warps = new Map<number, WarpDef>();
  readonly monstats: MonStat[] = [];
  readonly monById = new Map<string, MonStat>();
  readonly classes: ClassStats[] = [];
  readonly experience: number[][] = []; // [level][classIndex]
  readonly skills: SkillDef[] = [];
  readonly skillByName = new NameIndex<SkillDef>();
  readonly missiles: MissileDef[] = [];
  readonly missileByName = new NameIndex<MissileDef>();
  readonly states: StateDef[] = [];
  readonly stateByName = new NameIndex<StateDef>();
  readonly overlays: OverlayDef[] = [];
  readonly overlayByName = new NameIndex<OverlayDef>();
  readonly items = new Map<string, ItemBase>();
  readonly itemTypes = new Map<string, ItemTypeDef>();
  readonly stats: StatDef[] = [];
  readonly statByName = new NameIndex<StatDef>();
  readonly properties = new Map<string, PropertyDef>();
  readonly prefixes: AffixDef[] = [];
  readonly suffixes: AffixDef[] = [];
  readonly autoMagic: AffixDef[] = [];
  readonly rarePrefixes: RareAffixDef[] = [];
  readonly rareSuffixes: RareAffixDef[] = [];
  readonly uniques: UniqueDef[] = [];
  readonly setItems: SetItemDef[] = [];
  readonly treasureClasses = new Map<string, TreasureClass>();
  readonly gems = new Map<string, GemDef>();
  readonly runewords: RunewordDef[] = [];
  readonly objects: ObjectDef[] = [];
  readonly objByToken = new Map<string, ObjectDef>();
  readonly modeTokens: { player: string[]; monster: string[]; object: string[] } = { player: [], monster: [], object: [] };
  /** Fingerprint of every table's contents, so client and server can confirm they agree. */
  hash = '';
  /** ItemTypes equivalence closure: type → all types it counts as (itself included). */
  private readonly typeClosure = new Map<string, Set<string>>();
  /** MonLvl.txt rows by level (later data). */
  private readonly monLevels: ExcelRow[] = [];
  /** monstats rows by engine class index (see `monsterAt`). */
  private readonly engineRows: number[] = [];

  static async load(vfs: ArchiveSet): Promise<GameData> {
    const d = new GameData();
    let h = 0x811c9dc5;
    for (const name of TABLES) {
      const text = (await vfs.tryRead(`data\\global\\excel\\${name}.txt`)) ? await vfs.text(`data\\global\\excel\\${name}.txt`) : '';
      for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
      d.t[name] = new ExcelTable(text);
    }
    d.hash = (h >>> 0).toString(16).padStart(8, '0');
    d.build();
    return d;
  }

  private build(): void {
    const t = this.t;

    for (const r of t.Levels) {
      const id = r.int('Id', -1);
      if (id < 0) continue;
      const list = (prefix: string, n: number) => {
        const out: string[] = [];
        for (let i = 1; i <= n; i++) {
          const v = r.str(`${prefix}${i}`);
          if (v) out.push(v);
        }
        return out;
      };
      this.levels[id] = {
        id,
        name: r.str('Name'),
        levelName: r.str('LevelName'),
        warpName: r.str('LevelWarp'),
        entryFile: r.str('EntryFile'),
        act: r.int('Act'),
        drlg: r.int('DrlgType', 2) as 1 | 2 | 3,
        levelType: r.int('LevelType'),
        sizeX: [r.int('SizeX'), r.int('SizeX(N)', r.int('SizeX')), r.int('SizeX(H)', r.int('SizeX'))],
        sizeY: [r.int('SizeY'), r.int('SizeY(N)', r.int('SizeY')), r.int('SizeY(H)', r.int('SizeY'))],
        offsetX: r.int('OffsetX'),
        offsetY: r.int('OffsetY'),
        depend: r.int('Depend'),
        isInside: r.bool('IsInside'),
        vis: [0, 1, 2, 3, 4, 5, 6, 7].map((i) => r.int(`Vis${i}`)),
        warp: [0, 1, 2, 3, 4, 5, 6, 7].map((i) => r.int(`Warp${i}`, -1)),
        waypoint: r.int('Waypoint', 255),
        monLvl: [r.int('MonLvl1'), r.int('MonLvl2'), r.int('MonLvl3')],
        monLvlEx: [r.int('MonLvl1Ex'), r.int('MonLvl2Ex'), r.int('MonLvl3Ex')],
        monDen: r.int('MonDen'),
        uniqueMin: r.int('MonUMin'),
        uniqueMax: r.int('MonUMax'),
        // M1../S1../U1.. in 1.07–1.09; later data: mon1.. (normal games), umon1.., no nests.
        monsters: r.has('M1') ? list('M', 25) : list('mon', 25),
        specials: list('S', 25),
        uniques: r.has('U1') ? list('U', 25) : list('umon', 25),
        spawn: levelSpawn(r),
        // Critters: C1../CA1.. in 1.07–1.09, cmon1../cpct1.. later.
        critters: [1, 2, 3, 4, 5].map((i) => ({ id: r.str(`C${i}`) || r.str(`cmon${i}`), chance: r.int(r.has(`CA${i}`) ? `CA${i}` : `cpct${i}`) })).filter((c) => c.id),
        light: { intensity: r.int('Intensity'), r: r.int('Red'), g: r.int('Green'), b: r.int('Blue') },
        isTown: /town/i.test(r.str('Name')) && !/transition/i.test(r.str('Name')),
        soundEnv: r.int('SoundEnv'),
        objGroups: [0, 1, 2, 3, 4, 5, 6, 7].map((i) => ({ group: r.int(`ObjGrp${i}`), prob: r.int(`ObjPrb${i}`) })).filter((g) => g.group),
        quest: r.int('Quest'),
        rain: r.bool('Rain'),
        row: r,
      };
    }

    for (const r of t.LvlPrest) {
      const def = r.int('Def', -1);
      if (def < 0) continue;
      const files: string[] = [];
      for (let i = 1; i <= 6; i++) {
        const f = r.str(`File${i}`);
        if (f && f !== '0') files.push(dt1Path(f));
      }
      this.presets.set(def, {
        def,
        name: r.str('Name'),
        levelId: r.int('LevelId'),
        populate: r.bool('Populate'),
        outdoors: r.bool('Outdoors'),
        killEdge: r.bool('KillEdge'),
        fillBlanks: r.bool('FillBlanks'),
        sizeX: r.int('SizeX'),
        sizeY: r.int('SizeY'),
        scan: r.bool('Scan'),
        files,
        dt1Mask: r.int('Dt1Mask') >>> 0,
        row: r,
      });
    }

    for (const r of t.LvlTypes) {
      const id = r.int('Id', -1);
      if (id < 0) continue;
      const files: string[] = [];
      for (let i = 1; i <= 32; i++) {
        const f = r.str(`File ${i}`);
        files.push(f && f !== '0' ? dt1Path(f) : '');
      }
      this.lvlTypes[id] = { id, name: r.str('Name'), act: r.int('Act'), files };
    }

    for (const r of t.LvlMaze) {
      const level = r.int('Level', -1);
      if (level < 0) continue;
      this.mazes.set(level, {
        level,
        rooms: [r.int('Rooms'), r.int('Rooms(N)', r.int('Rooms')), r.int('Rooms(H)', r.int('Rooms'))],
        sizeX: r.int('SizeX'),
        sizeY: r.int('SizeY'),
        merge: r.int('Merge'),
      });
    }

    for (const r of t.LvlWarp) {
      const id = r.int('Id', -1);
      if (id < 0) continue;
      this.warps.set(id, {
        id,
        name: r.str('Name'),
        selectX: r.int('SelectX'),
        selectY: r.int('SelectY'),
        selectDX: r.int('SelectDX'),
        selectDY: r.int('SelectDY'),
        exitWalkX: r.int('ExitWalkX'),
        exitWalkY: r.int('ExitWalkY'),
        offsetX: r.int('OffsetX'),
        offsetY: r.int('OffsetY'),
        litVersion: r.bool('LitVersion'),
        tiles: r.int('Tiles'),
        direction: r.str('Direction'),
      });
    }

    this.buildMonsters();
    this.resolveLevelMonsters();
    this.buildClasses();
    this.buildSkills();
    this.buildEffects();
    this.buildItems();
    this.buildObjects();

    this.modeTokens.player = [...t.PlrMode].map((r) => r.str('Token'));
    this.modeTokens.monster = [...t.MonMode].map((r) => r.str('Token'));
    this.modeTokens.object = [...t.ObjMode].map((r) => r.str('Token'));
    this.linkMonsters();
  }

  private buildMonsters(): void {
    // 1.10 moved the graphics columns (layers, modes, size, selection, light…) to monstats2.txt,
    // one row per MonStatsEx id; 1.07–1.09 keep them in monstats itself.
    const gfx = new Map<string, ExcelRow>();
    for (const g of this.t.monstats2 ?? []) if (g.str('Id')) gfx.set(g.str('Id').toLowerCase(), g);
    const ratios = this.t.monstats.has('MonStatsEx') || this.t.monstats.has('hcIdx');
    let i = 0;
    for (const r of this.t.monstats) {
      const hcIdx = i++;
      // 1.07–1.09 name the class "Class", later data "Id" (rows are then the engine's indices).
      const tableId = r.str('Class') || r.str('Id');
      if (!tableId) continue;
      const id = (!r.has('Class') && LEGACY_CLASS[hcIdx]) || tableId;
      const g = gfx.get((r.str('MonStatsEx') || tableId).toLowerCase()) ?? r;
      // A column under its 1.09 name, else its later name (same meaning).
      const pick = (row: ExcelRow, old: string, later: string) => (row.has(old) ? old : later);
      const comps: Record<string, string[]> = {};
      for (const c of ['HD', 'TR', 'LG', 'RA', 'LA', 'RH', 'LH', 'SH', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8']) {
        // A layer that is on with no variants listed draws its first token, "lit" (the Valkyrie:
        // TR and S1 on, TRv and S1v blank; she was invisible).
        const v = g.has(`${c}v`) ? (g.bool(c) ? g.str(`${c}v`) || 'lit' : '') : g.str(c);
        if (!v) continue;
        // 1.07 marks which layers a monster has with 0/1 flags (armor tokens default to "lit");
        // later data lists the token variants per layer instead.
        if (/^\d+$/.test(v)) {
          if (Number(v) > 0) comps[c] = ['lit'];
        } else comps[c] = v.replace(/"/g, '').split(',').map((s) => s.trim().toLowerCase()).filter((s) => s && s !== 'nil');
      }
      const modes: Record<string, boolean> = {};
      for (const m of ['DH', 'N', 'W', 'GH', 'A1', 'A2', 'B', 'C', 'S1', 'S2', 'S3', 'S4', 'DD', 'KB', 'SQ', 'RN'])
        modes[m] = g.has(`Mode${m}`) ? g.bool(`Mode${m}`) : g.bool(`m${MODE_TOKEN[m]}`);
      // Attack to-hit: "A1ToHit" (1.07–1.09), "A1TH" later.
      const dmg = (p: string, d: number): Dmg => ({
        min: r.int(`${p}MinD${DIFF_SUFFIX[d]}`),
        max: r.int(`${p}MaxD${DIFF_SUFFIX[d]}`),
        toHit: r.int(`${pick(r, `${p}ToHit`, `${p}TH`)}${DIFF_SUFFIX[d]}`),
      });
      // Resistances: "FireResist" (1.07–1.09), "ResFi" later.
      const res = (d: number): Resists => {
        const col = (old: string, later: string) => r.int(`${pick(r, old, later)}${DIFF_SUFFIX[d]}`);
        return { dmg: col('DamageResist', 'ResDm'), magic: col('MagicResist', 'ResMa'), fire: col('FireResist', 'ResFi'), light: col('LightResist', 'ResLi'), cold: col('ColdResist', 'ResCo'), poison: col('PoisonResist', 'ResPo') };
      };
      const tc = (d: number) => [1, 2, 3, 4].map((k) => r.str(`TreasureClass${k}${DIFF_SUFFIX[d]}`));
      // 1.07 names missiles by Missiles.txt row; 65535 (or blank) means none. Later data names
      // them (resolved by `linkMonsters`).
      const miss = (c: string) => {
        const v = r.int(c, -1);
        return v >= 0 && v < 65535 ? v : -1;
      };
      const skills: MonStat['skills'] = [];
      for (let k = 1; k <= 8; k++) {
        const s = r.str(`Skill${k}`);
        if (s) skills.push({ skill: s, level: r.int(pick(r, `Skill${k}Lvl`, `Sk${k}lvl`), 1), mode: r.str(pick(r, `Skill${k}Seq`, `Sk${k}mode`)) });
      }
      // Elemental damage: one slot in 1.07–1.09 (numeric mode and type), three in later data
      // (mode and type tokens, resolved by `linkMonsters`; the first is used).
      const el = r.has('ElMode') ? '' : '1';
      const m: MonStat = {
        hcIdx,
        id,
        tableId,
        baseId: r.str('BaseId'),
        nextInClass: r.str('NextInClass'),
        nameStr: r.str('namco') || r.str('NameStr'),
        code: r.str('Code'),
        monType: r.str('Type') || r.str('MonType'),
        ai: r.str('AI'),
        // 1.10 blanked the 0s of 1.09 (and 1.09 has no "enabled" column: every class is enabled).
        enabled: r.has('enabled') ? r.bool('enabled') : true,
        velocity: r.int('Velocity'),
        run: r.int('Run'),
        level: [r.int('Level'), r.int('Level(N)'), r.int('Level(H)')],
        minGrp: r.int('MinGrp'),
        maxGrp: r.int('MaxGrp'),
        sizeX: g.int('SizeX', 1),
        sizeY: g.int('SizeY', 1),
        meleeRange: g.int('MeleeRng'),
        rarity: r.int('Rarity'),
        components: comps,
        baseW: g.str('BaseW').toLowerCase() || 'hth',
        modes,
        isMelee: r.bool('IsMelee'),
        isNpc: r.bool(pick(r, 'IsNPC', 'npc')),
        inTown: r.bool('InTown'),
        isCritter: g.bool(pick(g, 'IsCritter', 'critter')),
        isAttackable: g.bool('IsAtt'),
        isSelectable: g.bool('IsSel'),
        killable: r.has('Killable') ? r.bool('Killable') : true,
        noMap: g.bool('NoMap'),
        shadow: g.bool('Shadow'),
        light: [g.int('Light'), g.int(pick(g, 'LightR', 'light-r')), g.int(pick(g, 'LightG', 'light-g')), g.int(pick(g, 'LightB', 'light-b'))],
        boss: r.bool(pick(r, 'eBoss', 'boss')),
        undead: r.bool(pick(r, 'eHUndead', 'hUndead')) ? 2 : r.bool(pick(r, 'eLUndead', 'lUndead')) ? 1 : 0,
        demon: r.bool(pick(r, 'eDemon', 'demon')),
        missA1: r.str('MissA1'),
        missA2: r.str('MissA2'),
        missS1: r.str('MissS1'),
        skills,
        hp: [0, 1, 2].map((d) => [r.int(`MinHP${DIFF_SUFFIX[d]}`), r.int(`MaxHP${DIFF_SUFFIX[d]}`)]) as MonStat['hp'],
        ac: [r.int('AC'), r.int('AC(N)'), r.int('AC(H)')],
        exp: [r.int('Exp'), r.int('Exp(N)'), r.int('Exp(H)')],
        toBlock: r.int('ToBlock'),
        a1: [dmg('A1', 0), dmg('A1', 1), dmg('A1', 2)],
        a2: [dmg('A2', 0), dmg('A2', 1), dmg('A2', 2)],
        s1: [dmg('S1', 0), dmg('S1', 1), dmg('S1', 2)],
        resist: [res(0), res(1), res(2)],
        treasure: [tc(0), tc(1), tc(2)],
        aiParams: [1, 2, 3, 4, 5].map((k) => r.int(pick(r, `AIParam${k}`, `aip${k}`))),
        drain: r.int('Drain'),
        coldEffect: r.int('ColdEffect'),
        pixHeight: g.int('PixHeight'),
        interact: r.bool('Interact'),
        heightOverlay: g.int('OverlayHeight'),
        deathDamage: r.int(pick(r, 'DeathDamage', 'deathDmg')),
        hitClass: g.int('HitClass'),
        skillIds: skills.map((s, k) => ({ id: Number(s.skill), level: s.level, seq: r.int(`Skill${k + 1}Seq`) })).filter((s) => Number.isInteger(s.id) && s.id > 0),
        missiles: { A1: miss('MissA1'), A2: miss('MissA2'), S1: miss('MissS1'), S2: miss('MissS2'), S3: miss('MissS3'), S4: miss('MissS4'), C: miss('MissC'), SQ: miss('MissSQ') },
        elem: {
          mode: r.int(`El${el}Mode`),
          type: r.int(`El${el}Type`),
          overlay: r.int('ElOver'),
          pct: r.int(`El${el}Pct`),
          min: r.int(`El${el}MinD`),
          max: r.int(`El${el}MaxD`),
          dur: r.int(`El${el}Dur`),
        },
        align: r.int('Align'),
        petIgnore: r.bool('PetIgnore'),
        noUniqueShift: g.bool('NoUniqueShift'),
        ratios: ratios && !r.bool('noRatio'),
        row: r,
        gfx: g,
      };
      this.monstats[hcIdx] = m;
      for (const k of [id.toLowerCase(), tableId.toLowerCase()]) if (!this.monById.has(k)) this.monById.set(k, m);
    }
  }

  /**
   * Later monstats name what 1.07–1.09 number: skills (skills.txt names), missiles (Missiles.txt
   * names) and the elemental attack's mode and type (MonMode and ElemTypes codes). Resolves them
   * to the numbers the engine works with, once skills, missiles and the mode tokens are loaded.
   */
  private linkMonsters(): void {
    const elemIndex = new Map<string, number>();
    let e = 0;
    for (const r of this.t.ElemTypes) elemIndex.set(r.str('Code').toLowerCase(), e++);
    const modeIndex = (tok: string) => this.modeTokens.monster.findIndex((t) => t.toLowerCase() === tok.toLowerCase());
    for (const m of this.monstats) {
      if (!m) continue;
      if (!m.skillIds.length && m.skills.length) {
        m.skillIds = m.skills
          .map((s) => ({ id: this.skillByName.get(s.skill.toLowerCase())?.id ?? -1, level: s.level, seq: Math.max(0, modeIndex(s.mode)) }))
          .filter((s) => s.id > 0);
      }
      for (const k of Object.keys(m.missiles) as (keyof MonStat['missiles'])[]) {
        const name = m.row.str(`Miss${k}`);
        if (m.missiles[k] < 0 && name && !/^\d+$/.test(name)) m.missiles[k] = this.missileByName.get(name.toLowerCase())?.id ?? -1;
      }
      const el = m.row.has('El1Mode') ? m.row : null;
      if (el) {
        const mode = el.str('El1Mode');
        const type = el.str('El1Type');
        m.elem.mode = /^\d+$/.test(mode) ? Number(mode) : Math.max(0, modeIndex(mode));
        m.elem.type = /^\d+$/.test(type) ? Number(type) : (elemIndex.get(type.toLowerCase()) ?? 0);
      }
    }
  }

  /**
   * A monster's life, defense, experience and attacks at level `mlvl` on `difficulty`. 1.07–1.09
   * monstats hold the values themselves. 1.10 and later hold percentages of MonLvl.txt's values
   * for the monster's level, except rows marked noRatio. MonLvl has two sets: the "L-" columns
   * (single player, open and realm ladder games) and the plain ones (closed non-ladder realm
   * games: less life and defense on Nightmare/Hell); `ladderColumns` picks the first.
   */
  monsterValues(stat: MonStat, difficulty: number, mlvl: number, ladderColumns: boolean): MonValues {
    const d = Math.max(0, Math.min(2, difficulty));
    const raw = tableMonsterValues(stat, d);
    const lv = stat.ratios ? this.monLevel(mlvl) : undefined;
    if (!lv) return raw;
    const col = (c: string) => lv.int(`${ladderColumns ? 'L-' : ''}${c}${DIFF_SUFFIX[d]}`, lv.int(`${c}${DIFF_SUFFIX[d]}`));
    const of = (pct: number, base: number) => Math.floor((pct * base) / 100);
    const hp = col('HP'), dm = col('DM'), th = col('TH');
    const atk = (a: Dmg): Dmg => ({ min: of(a.min, dm), max: of(a.max, dm), toHit: of(a.toHit, th) });
    // Later data holds the elemental slots per difficulty, also as percentages of the damage base.
    const sfx = DIFF_SUFFIX[d];
    const el: [number, number] = stat.row.has('El1MinD') ? [of(stat.row.int(`El1MinD${sfx}`, stat.elem.min), dm), of(stat.row.int(`El1MaxD${sfx}`, stat.elem.max), dm)] : raw.elem;
    return { hp: [of(raw.hp[0], hp), of(raw.hp[1], hp)], ac: of(raw.ac, col('AC')), exp: of(raw.exp, col('XP')), a1: atk(raw.a1), a2: atk(raw.a2), s1: atk(raw.s1), elem: el };
  }

  /**
   * One MonLvl.txt value (`HP`, `AC`, `TH`, `DM`, `XP`) at a monster level and difficulty, from
   * the "L-" set or the plain one (see `monsterValues`); 0 where the data has no MonLvl.txt.
   */
  monLevelValue(mlvl: number, column: string, difficulty: number, ladderColumns: boolean): number {
    const lv = this.monLevel(mlvl);
    if (!lv) return 0;
    const sfx = DIFF_SUFFIX[Math.max(0, Math.min(2, difficulty))];
    return lv.int(`${ladderColumns ? 'L-' : ''}${column}${sfx}`, lv.int(`${column}${sfx}`));
  }

  /** MonLvl.txt row of a monster level (later data only). */
  private monLevel(mlvl: number): ExcelRow | undefined {
    if (!this.monLevels.length) for (const r of this.t.MonLvl ?? []) this.monLevels[r.int('Level')] = r;
    return this.monLevels[Math.max(1, Math.min(mlvl, this.monLevels.length - 1))];
  }

  /**
   * The monster with the engine's class index (monstats `hcIdx`). The engine numbers monstats
   * without the "Expansion" divider line, which 1.07–1.09's table keeps as a row; later tables
   * drop it, so their rows are the engine's indices.
   */
  monsterAt(engineIndex: number): MonStat | undefined {
    if (!this.engineRows.length) {
      const t = this.t.monstats;
      for (let i = 0; i < t.length; i++) if ((t.row(i).str('Class') || t.row(i).str('Id')).toLowerCase() !== 'expansion') this.engineRows.push(i);
    }
    const r = this.engineRows[engineIndex];
    return r === undefined ? undefined : this.monstats[r];
  }

  /**
   * 1.07–1.09 Levels.txt names monsters by number ("0" meaning empty); later data uses class ids.
   * The numbers are monstats `PopulateId`s, which restart at 0 after monstats' "Expansion" divider:
   * levels listed after Levels.txt's own "Expansion" divider (Act V) look them up among the
   * expansion monsters, the others among the classic ones (where PopulateId is the row number).
   * Data without PopulateId falls back to plain row numbers. Every list ends up as monstats rows
   * (spawn lists) or class ids (the rest).
   */
  private resolveLevelMonsters(): void {
    const ms = this.t.monstats;
    const divider = this.monstats.findIndex((m) => m?.id.toLowerCase() === 'expansion');
    const byPopulate: [Map<number, number>, Map<number, number>] = [new Map(), new Map()];
    if (ms.has('PopulateId')) {
      for (let i = 0; i < ms.length; i++) {
        const v = ms.row(i).str('PopulateId');
        if (!/^\d+$/.test(v) || !this.monstats[i]) continue;
        const section = byPopulate[divider >= 0 && i > divider ? 1 : 0];
        if (!section.has(Number(v))) section.set(Number(v), i);
      }
    }
    const lv = this.t.Levels;
    const expansionLevels = new Set<number>();
    if (lv.expansionStart >= 0) for (let i = lv.expansionStart; i < lv.length; i++) expansionLevels.add(lv.row(i).int('Id', -1));
    for (const l of this.levels) {
      if (!l) continue;
      const populate = byPopulate[expansionLevels.has(l.id) ? 1 : 0];
      // A numeric reference → monstats row (-1: none).
      const numbered = (n: number) => (populate.size ? (populate.get(n) ?? -1) : n);
      const resolve = (v: string): string | null => {
        if (!/^\d+$/.test(v)) return v;
        const n = Number(v);
        if (n === 0) return null;
        return this.monstats[numbered(n)]?.id ?? null;
      };
      const list = (a: string[]) => a.map(resolve).filter((x): x is string => !!x);
      const row = (v: string) => (/^\d+$/.test(v) ? numbered(Number(v)) : (this.monById.get(v.toLowerCase())?.hcIdx ?? -1));
      const rows = (a: string[]) => a.map(row).filter((n) => n >= 0 && this.monstats[n]);
      l.monsters = list(l.monsters);
      l.specials = list(l.specials);
      l.uniques = list(l.uniques);
      l.critters = l.critters.map((c) => ({ ...c, id: resolve(c.id) ?? '' })).filter((c) => c.id);
      const sp = l.spawn as LevelSpawn & { raw?: { normal: string[]; nightmare: string[]; nests: string[]; uniques: string[] } };
      const raw = sp.raw;
      if (raw) {
        sp.normal = rows(raw.normal);
        sp.nightmare = raw.nightmare.length ? rows(raw.nightmare) : sp.normal.slice();
        sp.uniques = rows(raw.uniques);
        sp.nests = raw.normal.map((_, i) => (raw.nests[i] && raw.nests[i] !== '0' ? row(raw.nests[i]) : -1));
        delete sp.raw;
      }
    }
  }

  private buildClasses(): void {
    let idx = 0;
    for (const r of this.t.charstats) {
      const name = r.str('class');
      if (!name || name === 'Expansion') continue;
      const startItems: ClassStats['startItems'] = [];
      for (let i = 1; i <= 10; i++) {
        const code = r.str(`item${i}`);
        if (code && code !== '0') startItems.push({ code, loc: r.str(`item${i}loc`), count: r.int(`item${i}count`, 1) });
      }
      this.classes.push({
        index: idx++,
        name,
        str: r.int('str'),
        dex: r.int('dex'),
        int: r.int('int'),
        vit: r.int('vit'),
        stamina: r.int('stamina'),
        hpAdd: r.int('hpadd'),
        manaRegen: r.int('ManaRegen', 300),
        toHitFactor: r.int('ToHitFactor'),
        walkVelocity: r.int('WalkVelocity', 6),
        runVelocity: r.int('RunVelocity', 9),
        runDrain: r.int('RunDrain', 20),
        lifePerLevel: r.int('LifePerLevel'),
        staminaPerLevel: r.int('StaminaPerLevel'),
        manaPerLevel: r.int('ManaPerLevel'),
        lifePerVitality: r.int('LifePerVitality'),
        staminaPerVitality: r.int('StaminaPerVitality'),
        manaPerMagic: r.int('ManaPerMagic'),
        blockFactor: r.int('BlockFactor'),
        startSkill: r.int('StartSkill'),
        baseWClass: r.str('baseWClass') || 'hth',
        startItems,
      });
    }
    for (const r of this.t.experience) {
      const lvl = r.str('Level');
      if (!/^\d+$/.test(lvl)) continue;
      this.experience[Number(lvl)] = this.classes.map((c) => r.num(c.name, 0));
    }
  }

  private buildSkills(): void {
    // 1.10 moved the skill tree position, icon and string keys to skilldesc.txt.
    const desc = new Map<string, ExcelRow>();
    for (const d of this.t.skilldesc ?? []) if (d.str('skilldesc')) desc.set(d.str('skilldesc').toLowerCase(), d);
    let row = 0;
    for (const r of this.t.skills) {
      const rowIndex = row++;
      const id = r.int('Id', -1);
      if (id < 0) continue;
      const sd = desc.get(r.str('skilldesc').toLowerCase()) ?? r;
      const s: SkillDef = {
        id,
        name: r.str('skill'),
        charClass: r.str('charclass'),
        anim: r.str('anim'),
        monAnim: r.str('monanim'),
        seqNum: r.int('seqnum'),
        isAura: r.bool(r.has('isaura') ? 'isaura' : 'aura'),
        passive: r.bool('Passive'),
        range: r.str('range') || 'none',
        reqLevel: r.int('reqlevel', 1),
        reqSkills: ['reqskill1', 'reqskill2', 'reqskill3'].map((c) => r.str(c)).filter(Boolean),
        page: sd.int('SkillPage'),
        treeRow: sd.int('SkillRow'),
        treeColumn: sd.int('SkillColumn'),
        iconCel: sd.int('IconCel'),
        leftSkill: r.bool(r.has('leftskl') ? 'leftskl' : 'leftskill'),
        mana: r.int('mana'),
        lvlMana: r.int('lvlmana'),
        // Blank cells are 0, as the engine reads them (TXTFIELD_BYTE): 1.10 blanked the 0s of 1.09.
        // No 1.14d row with a mana cost has a blank manashift; a blank SrcDam passes the whole
        // weapon in a blow and nothing in a missile (srcDamShare in the sim).
        manaShift: r.int('manashift'),
        minMana: r.int('minmana'),
        inTown: r.bool('InTown'),
        delay: r.int('Delay'),
        params: [1, 2, 3, 4, 5, 6, 7, 8].map((k) => r.int(`Param${k}`)),
        toHit: r.int('ToHit'),
        levToHit: r.int('LevToHit'),
        // A blank cell is 0, as the engine reads it: 1.10 blanked the cells 1.09 wrote as 0.
        hitShift: r.int('HitShift'),
        srcDam: r.int('SrcDam'),
        minDam: r.int('MinDam'),
        maxDam: r.int('MaxDam'),
        levDam: levelBrackets(r, 'LevDam', 'MinLevDam'),
        levDamMax: levelBrackets(r, 'LevDam', 'MaxLevDam'),
        eType: r.str('EType'),
        eMin: r.int('EMin'),
        eMax: r.int('EMax'),
        eLev: levelBrackets(r, 'ELev', 'EMinLev'),
        eLevMax: levelBrackets(r, 'ELev', 'EMaxLev'),
        eLen: r.int('ELen'),
        eLevLen: [r.int('ELevLen1'), r.int('ELevLen2'), r.int('ELevLen3')],
        srvMissile: r.str('srvmissile') || r.str('cltmissile'),
        state: r.str('State1') || r.str('aurastate'),
        attackRank: r.int('AttackRank'),
        itemClasses: [1, 2, 3, 4, 5, 6].map((k) => r.str(`itemclass${k}`)).filter(Boolean),
        strIndex: rowIndex,
        nameKey: sd.str('str name') || `skillname${rowIndex}`,
        descKey: sd.str('str long') || `skillld${rowIndex}`,
        casterState: r.str('caster state') || r.str('aurastate'),
        states: ['State1', 'State2', 'State3'].map((c) => r.str(c)).filter((v) => v && v !== '0'),
        selectDead: r.bool(r.has('SelectDead') ? 'SelectDead' : 'TargetCorpse'),
        searchMonsterNear: r.bool(r.has('SearchMonsterNear') ? 'SearchMonsterNear' : 'SearchEnemyNear'),
        targetableOnly: r.bool('TargetableOnly'),
        targetPet: r.bool('TargetPet'),
        targetAlly: r.bool('TargetAlly'),
        interrupt: r.bool('Interrupt'),
        periodic: r.bool('Periodic'),
        finishing: r.bool('Finishing'),
        monster: r.bool('Monster'),
        row: r,
        desc: sd === r ? undefined : sd,
      };
      this.skills[id] = s;
      this.skillByName.set(s.name.toLowerCase(), s);
    }
    // Missile Id values repeat in the LoD table, so missiles are keyed by row position (unique and
    // identical on server and client).
    let missileRow = 0;
    for (const r of this.t.Missiles) {
      const id = missileRow++;
      if (!r.str('Missile')) continue;
      const m: MissileDef = {
        id,
        name: r.str('Missile'),
        vel: r.int('Vel'),
        maxVel: r.int('MaxVel'),
        accel: r.int('Accel'),
        range: r.int('Range'),
        levRange: r.int('LevRange'),
        light: r.int('Light'),
        rgb: [r.int('Red'), r.int('Green'), r.int('Blue')],
        celFile: r.str('CelFile'),
        animLen: r.int('AnimLen', 1),
        animSpeed: r.int('AnimSpeed', 16),
        loopAnim: r.bool('LoopAnim'),
        collideType: r.int('CollideType'),
        collideKill: r.bool('CollideKill'),
        size: r.int('Size'),
        explosion: r.bool('Explosion'),
        pierce: r.bool('Pierce'),
        trans: r.int('Trans'),
        numDirections: r.int('NumDirections', 1),
        subMissile: r.str('SubMissile1') || r.str('SubMissile'),
        explosionMissile: r.str('ExplosionMissile'),
        hitClass: r.int('HitClass'),
        minDamage: r.int('MinDamage'),
        maxDamage: r.int('MaxDamage'),
        levDam: levelBrackets(r, 'LevDam', 'MinLevDam'),
        levDamMax: levelBrackets(r, 'LevDam', 'MaxLevDam'),
        eType: r.str('EType'),
        eMin: r.int('EMin'),
        eMax: r.int('Emax'),
        eLev: levelBrackets(r, 'ELev', 'MinELev'),
        eLevMax: levelBrackets(r, 'ELev', 'MaxELev'),
        eLen: r.int('ELen'),
        srcDamage: r.int('SrcDamage'),
        half2HSrc: r.bool('Half2HSrc'),
        skill: r.str('Skill'),
        params: [r.int('Param1'), r.int('Param2')],
        nextHit: r.bool('NextHit'),
        nextDelay: r.int('NextDelay'),
        toHit: r.has('ToHit') ? r.int('ToHit') > 0 : true,
        collideFriend: r.bool('CollideFriend'),
        qty: r.int('Qty'),
        // Blank is 0 (1.10 blanked the 0s of 1.09: Andariel's spray would hit for 1792 otherwise).
        hitShift: r.int('HitShift'),
        eLen2: [r.int('ELevLen1'), r.int('ELevLen2'), r.int('ELevLen3')],
        row: r,
      };
      this.missiles[id] = m;
      this.missileByName.set(m.name.toLowerCase(), m);
    }
    // 1.07 links missiles to skills from the missile side (Missiles.Skill = skills Id) and names
    // skill animations with words; normalize both onto the skill.
    const firstMissile = new Map<number, MissileDef>();
    for (const m of this.missiles) {
      if (!m) continue;
      const sid = Number(m.skill);
      if (Number.isInteger(sid) && sid > 0 && !firstMissile.has(sid)) firstMissile.set(sid, m);
    }
    const ANIM: Record<string, string> = { cast: 'SC', attack: 'A1', a1: 'A1', a2: 'A2', throw: 'TH', kick: 'KK', seq: 'A1', sk1: 'S1', sk2: 'S2', sk3: 'S3', sk4: 'S4', aura: '', none: '' };
    for (const s of this.skills) {
      if (!s) continue;
      if (!s.srvMissile) s.srvMissile = firstMissile.get(s.id)?.name ?? '';
      const a = s.anim.toLowerCase();
      if (a in ANIM) s.anim = ANIM[a];
    }
  }

  private buildEffects(): void {
    this.states.push(...buildStates(this.t.states));
    for (const s of this.states) if (s) this.stateByName.set(s.name.toLowerCase(), s);
    this.overlays.push(...buildOverlays(this.t.Overlay));
    for (const o of this.overlays) if (o) this.overlayByName.set(o.name.toLowerCase(), o);
  }

  private buildItems(): void {
    const t = this.t;
    for (const r of t.ItemTypes) {
      const code = r.str('Code');
      if (!code) continue;
      this.itemTypes.set(code, {
        code,
        name: r.str('ItemType'),
        equiv: [r.str('Equiv1'), r.str('Equiv2')].filter(Boolean),
        bodyLocs: [r.str('BodyLoc1'), r.str('BodyLoc2')].filter(Boolean),
        beltable: r.bool('Beltable'),
        throwable: r.bool('Throwable'),
        quiver: r.str('Quiver'),
        shoots: r.str('Shoots'),
        normal: r.bool('Normal'),
        magic: r.bool('Magic'),
        rare: r.bool('Rare'),
        maxSockets: [r.int('MaxSock1'), r.int('MaxSock25'), r.int('MaxSock40')],
        staffMods: r.str('StaffMods'),
        cls: r.str('Class'),
        varInvGfx: r.int('VarInvGfx'),
        invGfx: [1, 2, 3, 4, 5, 6].map((k) => r.str(`InvGfx${k}`)).filter(Boolean),
        storePage: r.str('StorePage'),
        autoStack: r.bool('AutoStack'),
        repair: r.bool('Repair'),
        identify: r.bool('Identify'),
        charm: r.bool('Charm'),
        gem: r.bool('Gem'),
        treasureClass: r.bool('TreasureClass'),
        rarity: r.int('Rarity', 1),
      });
    }

    const base = (r: ExcelRow, category: ItemCategory): ItemBase => {
      const vendors: ItemBase['vendors'] = {};
      for (const v of ITEM_VENDORS) {
        const min = r.int(`${v}Min`), max = r.int(`${v}Max`), mmin = r.int(`${v}MagicMin`), mmax = r.int(`${v}MagicMax`);
        if (min || max || mmin || mmax) vendors[v.toLowerCase()] = { min, max, magicMin: mmin, magicMax: mmax, magicLvl: r.int(`${v}MagicLvl`) };
      }
      return {
        code: r.str('code'),
        name: category === 'misc' ? r.str('namco') || r.str('name') : r.str('namestr') || r.str('code'),
        category,
        type: r.str('type'),
        type2: r.str('type2'),
        level: r.int('level'),
        levelReq: r.int('levelreq'),
        rarity: r.int('rarity'),
        spawnable: r.str('spawnable') !== '0' && r.int('quest') === 0, // quest items never drop randomly
        cost: r.int('cost'),
        gambleCost: r.int('gamble cost'),
        invWidth: r.int('invwidth', 1),
        invHeight: r.int('invheight', 1),
        invFile: r.str('invfile'),
        uniqueInvFile: r.str('uniqueinvfile'),
        setInvFile: r.str('setinvfile'),
        flippyFile: r.str('flippyfile'),
        alternateGfx: r.str('alternategfx') || r.str('alternateGfx'),
        component: r.int('component', 16),
        wclass: r.str('wclass'),
        wclass2h: r.str('2handedwclass'),
        minDam: r.int('mindam'),
        maxDam: r.int('maxdam'),
        twoHandMinDam: r.int('2handmindam'),
        twoHandMaxDam: r.int('2handmaxdam'),
        minMisDam: r.int('minmisdam'),
        maxMisDam: r.int('maxmisdam'),
        oneOrTwoHanded: r.bool('1or2handed'),
        twoHanded: r.bool('2handed'),
        rangeAdder: r.int('rangeadder'),
        speed: r.int('speed'),
        strBonus: r.int('StrBonus'),
        dexBonus: r.int('DexBonus'),
        reqStr: r.int('reqstr'),
        reqDex: r.int('reqdex'),
        durability: r.int('durability'),
        noDurability: r.bool('nodurability'),
        minAc: r.int('minac'),
        maxAc: r.int('maxac'),
        block: r.int('block'),
        gemSockets: r.int('gemsockets'),
        stackable: r.bool('stackable'),
        minStack: r.int('minstack'),
        maxStack: r.int('maxstack'),
        spawnStack: r.int('spawnstack'),
        useable: r.bool('useable'),
        belt: r.int('belt'),
        autoBelt: r.bool('autobelt'),
        quest: r.int('quest'),
        normCode: r.str('normcode'),
        uberCode: r.str('ubercode'),
        ultraCode: r.str('ultracode'),
        magicLvl: r.int('magic lvl'),
        hitClass: r.str('hit class'),
        missileType: r.int('missiletype'),
        armorTokens: ['rArm', 'lArm', 'Torso', 'Legs', 'rSPad', 'lSPad'].map((c) => r.int(c)),
        vendors,
        row: r,
      };
    };
    for (const r of t.weapons) if (r.str('code')) this.items.set(r.str('code'), base(r, 'weapon'));
    for (const r of t.armor) if (r.str('code')) this.items.set(r.str('code'), base(r, 'armor'));
    for (const r of t.misc) if (r.str('code')) this.items.set(r.str('code'), base(r, 'misc'));

    for (const r of t.ItemStatCost) {
      const id = r.int('ID', -1);
      if (id < 0) continue;
      const s: StatDef = {
        id,
        name: r.str('Stat'),
        add: r.int('Add'),
        multiply: r.int('Multiply'),
        divide: r.int('Divide'),
        valShift: r.int('ValShift'),
        saveBits: r.int('Save Bits'),
        saveAdd: r.int('Save Add'),
        percent: r.bool('Percent'),
        // 1.07–1.09 PerLevelShift; later data: "op param" of the per-level ops (op base "level").
        perLevelShift: r.has('PerLevelShift') ? r.int('PerLevelShift') : r.str('op base') === 'level' ? r.int('op param') : 0,
        descPriority: r.int('descpriority'),
        descFunc: r.int('descfunc'),
        descVal: r.int('descval'),
        descStrPos: r.str('descstrpos'),
        descStrNeg: r.str('descstrneg'),
        descStr2: r.str('descstr2'),
        dgrp: r.int('dgrp'),
        dgrpFunc: r.int('dgrpfunc'),
        dgrpVal: r.int('dgrpval'),
        dgrpStrPos: r.str('dgrpstrpos'),
        dgrpStrNeg: r.str('dgrpstrneg'),
        dgrpStr2: r.str('dgrpstr2'),
        row: r,
      };
      this.stats[id] = s;
      this.statByName.set(s.name.toLowerCase(), s);
    }
    for (const r of t.Properties) {
      const code = r.str('code');
      if (!code) continue;
      const funcs: PropertyDef['funcs'] = [];
      for (let i = 1; i <= 7; i++) {
        const func = r.int(`func${i}`);
        if (!func) continue;
        funcs.push({ set: r.int(`set${i}`), val: r.int(`val${i}`), func, stat: r.str(`stat${i}`) });
      }
      this.properties.set(code, { code, funcs, row: r });
    }

    const mods = (r: ExcelRow, prefix: string, n: number, style: 'mod' | 'item' | 'prop' | 'aprop'): Mod[] => {
      const out: Mod[] = [];
      for (let i = 1; i <= n; i++) {
        const k = style === 'mod' ? `${prefix}${i}` : style === 'item' ? `ItemMod${i}` : style === 'prop' ? `prop${i}` : prefix + i;
        const code = style === 'mod' ? r.str(`${k}code`) : style === 'item' ? r.str(`${k}Code`) : r.str(k);
        if (!code) continue;
        const pre = style === 'mod' ? k : style === 'item' ? k : '';
        out.push({
          code,
          param: style === 'prop' ? r.str(`par${i}`) : style === 'aprop' ? r.str(`apar${prefix.slice(5)}`) : r.str(`${pre}param`) || r.str(`${pre}Param`),
          min: style === 'prop' ? r.int(`min${i}`) : r.int(`${pre}min`) || r.int(`${pre}Min`),
          max: style === 'prop' ? r.int(`max${i}`) : r.int(`${pre}max`) || r.int(`${pre}Max`),
        });
      }
      return out;
    };
    const affixes = (table: ExcelTable, into: AffixDef[]) => {
      let index = 0;
      for (const r of table) {
        const name = r.str('Name');
        const i = index++;
        if (!name) continue;
        into.push({
          index: i,
          name,
          spawnable: r.bool('spawnable'),
          rare: r.bool('rare'),
          level: r.int('level'),
          maxLevel: r.int('maxlevel', 0),
          levelReq: r.int('levelreq'),
          classSpecific: r.str('classspecific'),
          // Blank is 0, never rolled (ItemsMagic: wFrequency == 0): the superseded version-0 rows.
          frequency: r.has('frequency') ? r.int('frequency') : 1,
          group: r.int('group'),
          mods: mods(r, 'mod', 3, 'mod'),
          itypes: [1, 2, 3, 4, 5, 6, 7].map((k) => r.str(`itype${k}`)).filter(Boolean),
          etypes: [1, 2, 3, 4, 5].map((k) => r.str(`etype${k}`)).filter(Boolean),
          transformColor: r.str('transformcolor'),
        });
      }
    };
    affixes(t.MagicPrefix, this.prefixes);
    affixes(t.MagicSuffix, this.suffixes);
    affixes(t.automagic, this.autoMagic);
    const rare = (table: ExcelTable, into: RareAffixDef[]) => {
      let index = 0;
      for (const r of table) {
        const name = r.str('name');
        const i = index++;
        if (!name) continue;
        into.push({
          index: i,
          name,
          itypes: [1, 2, 3, 4, 5, 6, 7].map((k) => r.str(`itype${k}`)).filter(Boolean),
          etypes: [1, 2, 3, 4].map((k) => r.str(`etype${k}`)).filter(Boolean),
        });
      }
    };
    rare(t.RarePrefix, this.rarePrefixes);
    rare(t.RareSuffix, this.rareSuffixes);

    let ui = 0;
    for (const r of t.UniqueItems) {
      const name = r.str('index') || r.str('name');
      const index = ui++;
      if (!name || !r.str('code')) continue;
      this.uniques.push({
        index,
        name,
        enabled: (r.has('enabled') ? r.bool('enabled') : true) && r.str('spawnable') !== '0',
        rarity: r.int('rarity'),
        level: r.int('lvl') || r.int('Level'),
        levelReq: r.int('lvl req') || r.int('LevelReq'),
        code: r.str('code'),
        invTransform: r.str('invtransform'),
        invFile: r.str('invfile'),
        mods: t.UniqueItems.has('prop1') ? mods(r, '', 12, 'prop') : mods(r, '', 10, 'item'),
        ladder: r.int('ladder') > 0,
        carry1: r.int('carry1') > 0,
      });
    }
    let si = 0;
    if (t.SetItems.has('Item1')) {
      // 1.07 layout: one row per set, items in Item1..Item6 column groups.
      for (const r of t.SetItems) {
        const set = r.str('Name');
        if (!set) continue;
        const grp = (p: string, keys: string[]): Mod[] =>
          keys.map((k) => ({ code: r.str(`${p}Code${k}`), param: r.str(`${p}Param${k}`), min: r.int(`${p}Min${k}`), max: r.int(`${p}Max${k}`) })).filter((m) => m.code);
        const setMods = [
          ...[2, 3, 4, 5].map((n) => ({ count: n, mods: grp('P', [String(n)]) })),
          { count: r.int('NumItems'), mods: grp('F', ['1', '2', '3', '4', '5', '6']) },
        ].filter((g) => g.mods.length);
        for (let k = 1; k <= 6; k++) {
          const code = r.str(`Item${k}`);
          if (!code) continue;
          this.setItems.push({
            index: si++,
            name: r.str(`Item${k} Suffix`),
            set,
            code,
            rarity: r.int(`Item${k} Rarity`, 1),
            level: r.int('level'),
            levelReq: r.int(`Item${k} Lvlreq`),
            invFile: '',
            mods: grp(`I${k}`, ['1', '2']),
            partialMods: grp(`I${k}`, ['A', 'B', 'C', 'D', 'E']),
            row: r,
            slot: k,
            setMods,
          });
        }
      }
    }
    // Later data keeps one row per set item and the set's own bonuses in Sets.txt: for wearing
    // 2..5 items (PCode<n>a/b) and the full set (FCode1..8).
    const setBonuses = new Map<string, { count: number; mods: Mod[] }[]>();
    for (const r of t.Sets ?? []) {
      const grp = (keys: string[], p: string) =>
        keys.map((k) => ({ code: r.str(`${p}Code${k}`), param: r.str(`${p}Param${k}`), min: r.int(`${p}Min${k}`), max: r.int(`${p}Max${k}`) })).filter((m) => m.code);
      const partial = [2, 3, 4, 5].map((n) => ({ count: n, mods: grp([`${n}a`, `${n}b`], 'P') }));
      setBonuses.set(r.str('index'), [...partial, { count: 0, mods: grp(['1', '2', '3', '4', '5', '6', '7', '8'], 'F') }].filter((g) => g.mods.length));
    }
    const setSize = new Map<string, number>();
    for (const r of t.SetItems.has('Item1') ? [] : t.SetItems) if (r.str('set')) setSize.set(r.str('set'), (setSize.get(r.str('set')) ?? 0) + 1);
    for (const r of t.SetItems.has('Item1') ? [] : t.SetItems) {
      const name = r.str('index');
      const index = si++;
      if (!name || !r.str('item')) continue;
      const partialGroups = [1, 2, 3, 4, 5].map((k) =>
        ['a', 'b'].map((ab) => ({ code: r.str(`aprop${k}${ab}`), param: r.str(`apar${k}${ab}`), min: r.int(`amin${k}${ab}`), max: r.int(`amax${k}${ab}`) })).filter((m) => m.code),
      );
      this.setItems.push({
        index,
        name,
        set: r.str('set'),
        code: r.str('item'),
        rarity: r.int('rarity', 1),
        level: r.int('lvl'),
        levelReq: r.int('lvl req'),
        invFile: r.str('invfile'),
        mods: mods(r, '', 9, 'prop'),
        partialMods: partialGroups.flat(),
        partialGroups,
        addFunc: r.int('add func'),
        setMods: setBonuses.get(r.str('set'))?.map((g) => ({ count: g.count || (setSize.get(r.str('set')) ?? 0), mods: g.mods })),
        row: r,
      });
    }

    for (const r of t.TreasureClassEx) {
      const name = r.str('Treasure Class');
      if (!name) continue;
      const items: TreasureClass['items'] = [];
      for (let i = 1; i <= 10; i++) {
        const item = r.str(`Item${i}`);
        if (item) items.push({ item, prob: r.int(`Prob${i}`) });
      }
      this.treasureClasses.set(name, {
        name,
        picks: r.int('Picks', 1),
        unique: r.int('Unique'),
        set: r.int('Set'),
        rare: r.int('Rare'),
        magic: r.int('Magic'),
        noDrop: r.int('NoDrop'),
        items,
        // Blank is no group (0); 1.09 has no column.
        group: r.int('group'),
        level: r.int('level'),
      });
    }
    this.buildArmorWeaponTCs();

    let gi = 0;
    for (const r of t.gems) {
      const index = gi++;
      const code = r.str('code');
      if (!code) continue;
      const kind = (p: string) =>
        [1, 2, 3]
          .map((k) => ({ code: r.str(`${p}Mod${k}Code`), param: r.str(`${p}Mod${k}Param`), min: r.int(`${p}Mod${k}Min`), max: r.int(`${p}Mod${k}Max`) }))
          .filter((m) => m.code);
      this.gems.set(code, { code, index, name: r.str('name'), letter: r.str('letter'), mods: { weapon: kind('weapon'), helm: kind('helm'), shield: kind('shield') } });
    }
    let ri = 0;
    for (const r of t.Runes) {
      const index = ri++;
      const key = r.str('Name');
      if (!key) continue;
      const mods: Mod[] = [];
      for (let k = 1; k <= 7; k++) {
        const code = r.str(`T1Code${k}`);
        if (code) mods.push({ code, param: r.str(`T1Param${k}`), min: r.int(`T1Min${k}`), max: r.int(`T1Max${k}`) });
      }
      this.runewords.push({
        index,
        key,
        name: r.str('Rune Name') || key,
        complete: r.int('complete') === 1,
        server: r.int('server') > 0,
        itypes: [1, 2, 3, 4, 5, 6].map((k) => r.str(`itype${k}`)).filter(Boolean),
        etypes: [1, 2, 3].map((k) => r.str(`etype${k}`)).filter(Boolean),
        runes: [1, 2, 3, 4, 5, 6].map((k) => r.str(`Rune${k}`)).filter(Boolean),
        mods,
      });
    }
  }

  /**
   * The implicit "weapN"/"armoN"/"bowN"… treasure classes the engine derives from item levels: for
   * every ItemTypes row with TreasureClass = 1 and N = 3, 6 … 96, the spawnable non-quest bases of
   * that type (through Equiv chains) with N−3 < level ≤ N, each weighted by its own ItemTypes
   * Rarity (at least 1). Missile potions only join their own type's classes.
   */
  private buildArmorWeaponTCs(): void {
    const buckets = new Map<string, { code: string; prob: number }[]>();
    for (const it of this.items.values()) {
      if (!it.spawnable || it.quest > 0 || it.level <= 0 || it.level > 96) continue;
      const chain = new Set(this.typeChain(it.type));
      if (it.type2) for (const t of this.typeChain(it.type2)) chain.add(t);
      const tcTypes = new Set<string>();
      for (const ty of chain) if (this.itemTypes.get(ty)?.treasureClass) tcTypes.add(ty);
      const lvl = Math.ceil(it.level / 3) * 3;
      const prob = Math.max(1, this.itemTypes.get(it.type)?.rarity ?? 1);
      for (const ty of tcTypes) {
        if (chain.has('tpot') && ty !== 'tpot') continue;
        const name = `${ty}${lvl}`;
        if (!buckets.has(name)) buckets.set(name, []);
        buckets.get(name)!.push({ code: it.code, prob });
      }
    }
    for (const [name, codes] of buckets) {
      if (this.treasureClasses.has(name)) continue;
      this.treasureClasses.set(name, {
        name,
        picks: 1,
        unique: 0,
        set: 0,
        rare: 0,
        magic: 0,
        noDrop: 0,
        items: codes.map((c) => ({ item: c.code, prob: c.prob })),
        group: 0,
        level: 0,
      });
    }
  }

  private buildObjects(): void {
    const modeNames = ['NU', 'OP', 'ON', 'S1', 'S2', 'S3', 'S4', 'S5'];
    for (const r of this.t.objects) {
      const id = r.int('Id', -1);
      if (id < 0) continue;
      const arr = (p: string) => [0, 1, 2, 3, 4, 5, 6, 7].map((i) => r.int(`${p}${i}`));
      const comps: Record<string, boolean> = {};
      for (const c of ['HD', 'TR', 'LG', 'RA', 'LA', 'RH', 'LH', 'SH', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8']) if (r.bool(c)) comps[c] = true;
      const o: ObjectDef = {
        id,
        name: r.str('Name'),
        token: r.str('Token'),
        sizeX: r.int('SizeX'),
        sizeY: r.int('SizeY'),
        frameCnt: arr('FrameCnt'),
        frameDelta: arr('FrameDelta'),
        cycleAnim: arr('CycleAnim').map(Boolean),
        hasCollision: arr('HasCollision').map(Boolean),
        lit: arr('Lit'),
        start: arr('Start'),
        selectable: arr('Selectable').map(Boolean),
        isDoor: r.bool('IsDoor'),
        blocksVis: r.bool('BlocksVis'),
        orientation: r.int('Orientation'),
        trans: r.int('Trans'),
        operateRange: r.int('OperateRange'),
        operateFn: r.int('OperateFn'),
        populateFn: r.int('PopulateFn'),
        initFn: r.int('InitFn'),
        clientFn: r.int('ClientFn'),
        shrineFunction: r.int('ShrineFunction'),
        draw: r.int('Draw'),
        rgb: [r.int('Red'), r.int('Green'), r.int('Blue')],
        components: comps,
        xOffset: r.int('Xoffset'),
        yOffset: r.int('Yoffset'),
        subClass: r.int('SubClass'),
        autoMap: r.int('AutoMap'),
        drawUnder: r.bool('DrawUnder'),
        blockMissile: r.bool('BlockMissile'),
        openWarp: r.bool('OpenWarp'),
        modeNames,
        row: r,
      };
      this.objects[id] = o;
      if (o.token && !this.objByToken.has(o.token.toLowerCase())) this.objByToken.set(o.token.toLowerCase(), o);
    }
  }

  // ---- queries ------------------------------------------------------------------------------

  /** All item type codes `type` counts as (itself and its Equiv ancestors). */
  typeChain(type: string): Set<string> {
    let s = this.typeClosure.get(type);
    if (s) return s;
    s = new Set();
    const stack = [type];
    while (stack.length) {
      const t = stack.pop()!;
      if (!t || s.has(t)) continue;
      s.add(t);
      stack.push(...(this.itemTypes.get(t)?.equiv ?? []));
    }
    this.typeClosure.set(type, s);
    return s;
  }

  isType(itemCode: string, type: string): boolean {
    const it = this.items.get(itemCode);
    if (!it) return false;
    return this.typeChain(it.type).has(type) || (!!it.type2 && this.typeChain(it.type2).has(type));
  }

  /** Experience needed to reach `level` for a class index. */
  expFor(level: number, classIndex: number): number {
    if (level <= 1) return 0;
    return this.experience[level - 1]?.[classIndex] ?? Infinity;
  }

  /** Max character level from experience.txt ("MaxLvl" row is excluded by the numeric filter). */
  get maxLevel(): number {
    return this.experience.length - 1;
  }

  levelByName(name: string): LevelDef | undefined {
    return this.levels.find((l) => l?.name === name || l?.levelName === name);
  }

  presetFor(levelId: number): PresetDef | undefined {
    for (const p of this.presets.values()) if (p.levelId === levelId) return p;
    return undefined;
  }

  presetByName(name: string): PresetDef | undefined {
    for (const p of this.presets.values()) if (p.name === name) return p;
    return undefined;
  }
}
